import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';

import { MakeModelTap } from 'lib/Tracking/MakeModelMenu/MakeModelMenuEvents';
import { useMakeModelFilter, useTrimsFilter } from 'lib/hooks/useSrpFilters';
import { SourcePages, Features } from 'lib/EventSchema';
import { fetchVehicleTrims } from 'lib/AutolistAPI';
import useEvents from 'lib/hooks/useEvents';

import MakeModelMenu from 'components/Shared/MakeModelMenu';
import DropdownContainer from 'components/Shared/SearchFilter/DropdownContainer';
import ModalDropdownContainer from 'components/Shared/SearchFilter/ModalDropdownContainer';
import FilterContainer from 'components/Shared/SearchFilter/FilterContainer';
import { screen } from 'components/styles/variables';

import { FilterEventContext } from 'contexts/FilterEventContext';
import { MakeModel } from 'types/srpFilters';
import Make from 'types/Make';
import Model from 'types/Model';

import SrpEventContext from 'contexts/SrpEventContext';
import TrimFilter from './TrimFilter';

export interface MakeModelFilterProps {
  expanded?: boolean;
}

export default function MakeModelFilter({ expanded }: MakeModelFilterProps): JSX.Element {
  const logger = useEvents();
  const srpEventContext = useContext(SrpEventContext);
  const filterEventContext = useContext(FilterEventContext);
  const sourcePage =
    srpEventContext.sourcePage === SourcePages.Srp
      ? SourcePages.SrpFilter
      : SourcePages.SeoSrpFilter;

  const { t } = useTranslation();

  const { setValue, value } = useMakeModelFilter();
  const { setValue: setTrimsValue, value: trimsValue } = useTrimsFilter();

  const [trims, setTrims] = useState<string[]>([]);

  useEffect(() => {
    const fetchTrims = async (): Promise<void> => {
      const trimData: string[] = await fetchVehicleTrims(value);
      return setTrims([t('srp.filters.trim.allTrims'), ...trimData]);
    };
    if (value.make && value.model) {
      fetchTrims();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleClick = (): void => {
    logger.log(MakeModelTap(filterEventContext));
  };

  const handleSelect = (make?: Make, model?: Model): void => {
    setValue({
      make: make?.name,
      model: model?.name,
    });
    setTrimsValue([]);
  };

  function getPlaceholder(makeModel: MakeModel): string {
    if (!makeModel.make) {
      return t('srp.filters.makeModel');
    }

    if (makeModel.make && !makeModel.model) {
      return makeModel.make;
    }

    return `${makeModel.make} ${makeModel.model}`;
  }

  const trimFilterDisabled =
    isEmpty(value) || (!value.model && !value.make) || Boolean(value.make && !value.model);

  const getTrimPlaceholder = (): string => {
    if (!trimsValue || trimsValue.length === 0) {
      return t('srp.filters.trim.selectTrim');
    }
    return trimsValue.join(', ');
  };

  const handleAcceptClick = (trimChanges: string[]): void =>
    setTrimsValue(trims.filter((trim) => trimChanges.includes(trim)));

  return (
    <>
      <FilterEventContext.Provider value={{ sourcePage, feature: Features.makeModelTrim }}>
        <FilterContainer title={t('srp.filters.makeModelTrim')} expanded={expanded}>
          <div className="mmt-modal-container">
            <ModalDropdownContainer
              placeHolderTitle={getPlaceholder(value)}
              handleClick={handleClick}
            >
              <MakeModelMenu
                value={{
                  make: value.make,
                  model: value.model,
                }}
                onSelect={handleSelect}
                mobileHeight="compact"
              />
            </ModalDropdownContainer>

            <ModalDropdownContainer
              placeHolderTitle={getTrimPlaceholder()}
              disabled={trimFilterDisabled}
            >
              <TrimFilter
                trims={trims}
                selectedTrims={trimsValue}
                onAcceptClick={handleAcceptClick}
              />
            </ModalDropdownContainer>
          </div>

          <div className="mmt-dropdown-container">
            <DropdownContainer
              title={getPlaceholder(value)}
              resetsOnClose={true}
              onClick={handleClick}
            >
              <MakeModelMenu
                value={{
                  make: value.make,
                  model: value.model,
                }}
                onSelect={handleSelect}
                mobileHeight="compact"
              />
            </DropdownContainer>

            <DropdownContainer
              title={getTrimPlaceholder()}
              resetsOnClose={true}
              disabled={trimFilterDisabled}
            >
              <TrimFilter
                trims={trims}
                selectedTrims={trimsValue}
                onAcceptClick={handleAcceptClick}
              />
            </DropdownContainer>
          </div>
        </FilterContainer>
      </FilterEventContext.Provider>
      <style jsx>{`
        .mmt-modal-container {
          display: flex;
          flex-direction: column;
        }

        @media (min-width: ${screen.mobileMax}) {
          .mmt-modal-container {
            display: none;
          }
        }

        @media (max-width: ${screen.mobileMax}) {
          .mmt-dropdown-container {
            display: none;
          }
        }
      `}</style>
    </>
  );
}
